import React, { useMemo } from 'react';
import Button from 'components/Button';
import {
  LISTING_CONTACT_METHOD_ORDER,
  ListingContactMethod,
  type ListingContactMethodValue,
} from 'constants/listingContactMethod';
import { buildWhatsappClickToChatUrl } from 'utils/whatsappClickToChat';
import { formatPhoneForDisplay, normalizePhoneToInternationalDigits } from 'utils/phoneNumber';
import './ListingContactActions.css';

export interface ListingContactActionsProps {
  listingId?: string;
  listingCode: string;
  contactMethods: ListingContactMethodValue[];
  sellerPhoneNumber?: string | null;
  sellerWhatsappPhoneDigits?: string | null;
  isListingOwner: boolean;
  onDirectMessage: () => void;
}

function resolveActiveMethods(
  contactMethods: ListingContactMethodValue[],
  sellerPhoneNumber?: string | null,
  sellerWhatsappPhoneDigits?: string | null
): ListingContactMethodValue[] {
  return LISTING_CONTACT_METHOD_ORDER.filter((method) => {
    if (!contactMethods.includes(method)) return false;
    if (method === ListingContactMethod.DirectMessage) return true;
    if (method === ListingContactMethod.Phone) {
      return Boolean(
        normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '') ||
          sellerWhatsappPhoneDigits?.trim()
      );
    }
    if (method === ListingContactMethod.Whatsapp) {
      return Boolean(
        sellerWhatsappPhoneDigits?.trim() ||
          normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '')
      );
    }
    return false;
  });
}

const ListingContactActions: React.FC<ListingContactActionsProps> = ({
  listingCode,
  contactMethods,
  sellerPhoneNumber,
  sellerWhatsappPhoneDigits,
  isListingOwner,
  onDirectMessage,
}) => {
  const active = useMemo(
    () => resolveActiveMethods(contactMethods, sellerPhoneNumber, sellerWhatsappPhoneDigits),
    [contactMethods, sellerPhoneNumber, sellerWhatsappPhoneDigits]
  );

  if (isListingOwner || active.length === 0) return null;

  const phoneDigits =
    sellerWhatsappPhoneDigits ??
    normalizePhoneToInternationalDigits(sellerPhoneNumber ?? '') ??
    '';
  const phoneDisplay =
    sellerPhoneNumber?.trim() ||
    formatPhoneForDisplay(sellerPhoneNumber) ||
    (phoneDigits ? `+${phoneDigits}` : '');

  const showDirectMessage = active.includes(ListingContactMethod.DirectMessage);
  const showWhatsapp = active.includes(ListingContactMethod.Whatsapp) && Boolean(phoneDigits);
  const showPhone = active.includes(ListingContactMethod.Phone) && Boolean(phoneDigits);

  const waUrl = showWhatsapp ? buildWhatsappClickToChatUrl(phoneDigits, listingCode) : '';
  const telHref = showPhone ? `tel:+${phoneDigits}` : '';

  return (
    <div className="listing-contact-actions">
      {showDirectMessage && (
        <Button
          type="button"
          className="btn btn-primary listing-contact-actions__primary"
          onClick={onDirectMessage}
        >
          <i className="fas fa-comment-dots" aria-hidden="true" />
          Satıcıyla İletişime Geç
        </Button>
      )}

      {showWhatsapp && waUrl && (
        <div className="listing-contact-actions__whatsapp-panel">
          <div className="listing-contact-actions__whatsapp-title">WhatsApp üzerinden iletişime geç</div>
          <p className="listing-contact-actions__whatsapp-hint mb-0">
            Satıcıya WhatsApp&apos;tan mesaj göndermek için kullanın.
          </p>
          <a
            href={waUrl}
            className="listing-contact-actions__whatsapp-btn mt-3"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp" aria-hidden="true" />
            WhatsApp&apos;ı Aç
          </a>
        </div>
      )}

      {showPhone && telHref && (
        <a href={telHref} className="listing-contact-actions__phone" aria-label={`Telefon: ${phoneDisplay}`}>
          <i className="fas fa-phone" aria-hidden="true" />
          {phoneDisplay}
        </a>
      )}
    </div>
  );
};

export default ListingContactActions;
